import React, { useRef } from "react";
import { PostComposerProps } from "../interfaces/post.interface";
import { useAuthStore } from "../../../../../auth/auth.store";

const emojis = ["😊", "😂", "❤️", "👍", "🎉", "🔥", "✨", "💯"];

/**
 * Get a safe avatar URL.
 * Falls back to ui-avatars.com (initials-based) when no avatar exists.
 */
function getAvatarUrl(
  avatarUrl: string | null | undefined,
  name: string | null | undefined
): string {
  if (avatarUrl) return avatarUrl;

  const safeName = name?.trim() || "User";

  return `https://ui-avatars.com/api/?name=${encodeURIComponent(
    safeName
  )}&background=ccc&color=000&size=128&bold=true`;
}

const PostComposer: React.FC<PostComposerProps> = ({
  postText,
  setPostText,
  files,
  previews,
  emojiPickerOpen,
  toggleEmojiPicker,
  addEmoji,
  handleFilesSelect,
  removeFile,
  clearFiles,
  publishPost,
  isSaving,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const currentUser = useAuthStore((s) => s.currentUser);

  const hasContent =
    (postText?.trim().length ?? 0) > 0 || files.length > 0;

  const avatarSrc = getAvatarUrl(
    currentUser?.avatarUrl,
    currentUser?.name
  );

  return (
    <div className="mt-4 bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
      <div className="flex items-center gap-3 mb-3">
        <img
          src={avatarSrc}
          alt={currentUser?.name ?? "Profile"}
          className="w-10 h-10 rounded-full object-cover"
          onError={(e) => {
            (e.target as HTMLImageElement).src = getAvatarUrl(
              null,
              currentUser?.name
            );
          }}
        />

        <textarea
          value={postText}
          onChange={(e) => setPostText(e.target.value)}
          placeholder={`What's on your mind, ${
            currentUser?.name ?? "friend"
          }?`}
          className="flex-1 dark:bg-deepSlate rounded-xl px-4 py-2 text-lg resize-none focus:outline-none focus:bg-white focus:border focus:border-blue-600"
          rows={3}
        />
      </div>

      {/* Media Previews */}
      {previews.length > 0 && (
        <div
          className={`mb-3 grid gap-2 ${
            previews.length === 1
              ? "grid-cols-1"
              : previews.length === 2
              ? "grid-cols-2"
              : "grid-cols-3"
          }`}
        >
          {previews.map((url, index) => {
            const file = files[index];
            const isVideo = file?.type.startsWith("video/");

            return (
              <div
                key={index}
                className="relative aspect-square overflow-hidden rounded-lg"
              >
                {isVideo ? (
                  <video
                    src={url}
                    controls
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <img
                    src={url}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                )}

                <button
                  type="button"
                  onClick={() => removeFile(index)}
                  className="absolute top-2 right-2 bg-black/60 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-black"
                >
                  ×
                </button>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex items-center justify-between pt-3 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-1 px-3 py-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <span className="text-green-500">📷</span>
            <span className="text-gray-700 dark:text-muted text-sm">
              Photo/Video
            </span>
          </button>

          <div className="relative">
            <button
              type="button"
              onClick={toggleEmojiPicker}
              className="flex items-center gap-1 px-3 py-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <span className="text-yellow-500">😊</span>
              <span className="text-gray-700 dark:text-muted text-sm">
                Feeling/Activity
              </span>
            </button>

            {emojiPickerOpen && (
              <div className="absolute bottom-full left-0 bg-white dark:bg-dark_grey rounded shadow p-2 flex flex-wrap w-48 gap-1 z-50">
                {emojis.map((emoji) => (
                  <span
                    key={emoji}
                    onClick={() => addEmoji(emoji)}
                    className="cursor-pointer hover:bg-gray-100 p-1 rounded"
                  >
                    {emoji}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={publishPost}
          disabled={!hasContent || isSaving}
          className={`px-4 py-2 rounded font-semibold dark:text-midnight_text text-white ${
            !hasContent || isSaving
              ? "dark:bg-primary/80 bg-tealGreen/80 cursor-not-allowed"
              : "dark:bg-primary bg-tealGreen cursor-pointer"
          }`}
        >
          {isSaving ? "Posting..." : "Post"}
        </button>
      </div>

      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept="image/*,video/*"
        multiple
        onChange={handleFilesSelect}
      />
    </div>
  );
};

export default PostComposer;